/**
 * Base Parser Class
 * Provides common functionality and interface for all bank parsers
 */

import type { Transaction, ParserInfo, TransactionData } from '../types';

// Re-export for convenience
export type ParsedTransaction = Transaction;
export type { ParserInfo, TransactionData };

/**
 * Base Parser Class
 * Provides common functionality and interface for all bank parsers
 */
export class BaseParser {
  name: string;
  priority: number;

  constructor() {
    this.name = 'BaseParser';
    this.priority = 0; // Lower number = higher priority in detection
  }

  /**
   * Detect if this parser can handle the given CSV
   * @param header - Lowercased header line
   * @param bank - Bank hint from folder structure
   * @returns Confidence score 0-100 (0 = cannot parse, 100 = perfect match)
   */
  detect(header: string, bank: string): number {
    throw new Error('detect() must be implemented by subclass');
  }

  /**
   * Parse the CSV lines into transaction objects
   * @param lines - CSV lines (including header)
   * @param filename - Source filename
   * @returns Array of transaction objects
   */
  parse(lines: string[], filename: string): ParsedTransaction[] {
    throw new Error('parse() must be implemented by subclass');
  }

  /**
   * Get parser metadata
   * @returns Parser info
   */
  getInfo(): ParserInfo {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: []
    };
  }

  // ============================================================================
  // Shared Utility Methods
  // ============================================================================

  /**
   * Parse CSV line handling quotes and commas
   * @param line - CSV line to parse
   * @returns Parsed fields
   */
  parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  /**
   * Parse date in MM/DD/YYYY format
   * @param dateStr - Date string
   * @returns Parsed date
   */
  parseDate(dateStr: string): Date {
    if (!dateStr) return new Date();

    // Try MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }

    return new Date(dateStr);
  }

  /**
   * Parse date in ISO format YYYY-MM-DD
   * @param dateStr - Date string
   * @returns Parsed date
   */
  parseDateISO(dateStr: string): Date {
    if (!dateStr) return new Date();

    // Already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(dateStr);
    }

    // Fallback to general parser
    return this.parseDate(dateStr);
  }

  /**
   * Generate unique ID for transaction (non-deterministic - includes timestamp)
   * @param date - Transaction date
   * @param description - Transaction description
   * @param amount - Transaction amount
   * @returns Unique ID
   */
  generateId(date: string, description: string, amount: string | number): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 8);
    const str = `${date}-${description}-${amount}-${timestamp}-${random}`;
    return str.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  }

  /**
   * Generate deterministic ID for transaction (for duplicate detection)
   * @param date - Transaction date
   * @param description - Transaction description
   * @param amount - Transaction amount
   * @param bank - Bank/source name
   * @param balance - Optional account balance (included if available to distinguish duplicate transactions)
   * @returns Deterministic ID
   */
  generateDeterministicId(
    date: string,
    description: string,
    amount: string | number,
    bank: string = '',
    balance?: number | string
  ): string {
    // Include balance if provided (helps distinguish duplicate transactions with same date/description/amount)
    const balanceStr = balance !== undefined ? `-${balance}` : '';
    const str = `${date}-${description}-${amount}-${bank}${balanceStr}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const hashStr = Math.abs(hash).toString(36);
    const cleanStr = str.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 50);
    return `${cleanStr}_${hashStr}`;
  }

  /**
   * Create a standardized transaction object
   * @param data - Transaction data
   * @returns Normalized transaction
   */
  createTransaction(data: TransactionData): ParsedTransaction {
    const bank = data.bank || this.extractBankFromFilename(data.source || '') || '';
    return {
      id: data.id || this.generateId(data.date, data.description || 'Unknown', data.amount),
      dedupeId: data.dedupeId || this.generateDeterministicId(
        data.date,
        data.description || 'Unknown',
        data.amount,
        bank,
        data.balance
      ),
      date: data.date,
      postDate: data.postDate || data.date,
      description: data.description || 'Unknown',
      amount: typeof data.amount === 'string' ? parseFloat(data.amount) || 0 : data.amount || 0,
      type: data.type || 'Unknown',
      category: data.category || 'Uncategorized',
      person: data.person || 'Unknown',
      memo: data.memo || '',
      source: data.source || this.name,
      bank: bank,
      balance: data.balance !== undefined
        ? (typeof data.balance === 'string' ? parseFloat(data.balance) : data.balance)
        : undefined,
      rawData: data.rawData || []
    };
  }

  /**
   * Safe parse float with fallback
   * @param value - String to parse
   * @param defaultValue - Default value if parsing fails
   * @returns Parsed number
   */
  parseAmount(value: string, defaultValue: number = 0): number {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * Extract bank name from filename path like "Chase/John/file.csv"
   * @param filename - Full file path
   * @returns Bank name or null
   */
  extractBankFromFilename(filename: string): string | null {
    const parts = filename.split('/');
    if (parts.length > 0) {
      return parts[0].toLowerCase();
    }
    return null;
  }

  /**
   * Normalize bank name to standard format
   * @param bank - Raw bank name
   * @returns Normalized bank name
   */
  normalizeBankName(bank: string): string {
    if (!bank) return '';

    const normalized = bank.toLowerCase().trim();
    const mapping: Record<string, string> = {
      'american express': 'amex',
      'us bank': 'usbank',
      'capital one': 'capitalone'
    };

    return mapping[normalized] || normalized;
  }

  /**
   * Log parsing activity
   * @param message - Log message
   * @param level - Log level (info, warn, error)
   */
  log(message: string, level: 'info' | 'warn' | 'error' = 'info'): void {
    const prefix = `[${this.name}]`;
    switch (level) {
      case 'warn':
        console.warn(prefix, message);
        break;
      case 'error':
        console.error(prefix, message);
        break;
      default:
        console.log(prefix, message);
    }
  }
}
